// src/App.jsx
import React, { useState, useEffect, useRef } from "react";
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import { getCurrentUser } from "./services/graphService";
import { PurgePage, DelegatePage, ExfilPage, TracePage, ThreatPage, LogPage, ContainPage } from "./pages/Pages";

const TABS = [
  { id: "purge",    label: "Phishing purge",     color: "#00B2E3" },
  { id: "delegate", label: "Mailbox delegation", color: "#00BBB4" },
  { id: "exfil",    label: "Exfiltration audit", color: "#6ABF4B" },
  { id: "trace",    label: "Message trace",      color: "#8b7ef8" },
  { id: "threat",   label: "Threat intel",       color: "#FF9E18" },
  { id: "contain",  label: "User containment",   color: "#e05252" },
  { id: "log",      label: "Audit log",          color: "#7A97AB" },
];

// Use the actual uploaded IMA logo image
function IMALogo({ theme }) {
  const src = theme === "dark" ? "/ima-logo-white.png" : "/ima-logo.png";
  return (
    <img
      src={src}
      alt="The IMA Group"
      style={{ height: 28, width: "auto", objectFit: "contain" }}
      onError={e => {
        e.target.src = theme === "dark" ? "/ima-logo.png" : "/ima-logo-white.png";
      }}
    />
  );
}

function ThemeToggle({ theme, onToggle }) {
  return (
    <button
      onClick={onToggle}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        background: "var(--s2)", border: "1px solid var(--b2)",
        borderRadius: 999, padding: "5px 12px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 11, color: "var(--tx2)", fontFamily: "var(--sans)",
      }}
    >
      {theme === "dark" ? "☀ Light" : "☾ Dark"}
    </button>
  );
}

// Global scan indicator bar — shown on every tab while any operation runs
function GlobalScanBar({ scans }) {
  const active = Object.values(scans).filter(s => s?.running);
  if (active.length === 0) return null;
  return (
    <div style={{
      background: "rgba(0,178,227,0.1)",
      borderBottom: "1px solid rgba(0,178,227,0.25)",
      padding: "7px 20px",
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 11, color: "var(--primary)", fontFamily: "var(--mono)",
      flexShrink: 0,
    }}>
      <span style={{
        width: 10, height: 10, borderRadius: "50%",
        border: "1.5px solid var(--primary)", borderTopColor: "transparent",
        animation: "spin .7s linear infinite", flexShrink: 0,
      }}/>
      {active.map((s, i) => <span key={i}>{s.label}</span>)}
      <span style={{ marginLeft: "auto", color: "var(--tx3)" }}>
        You can safely switch tabs — operation continues in background
      </span>
    </div>
  );
}

// ─── Login screen ─────────────────────────────────────────────────────────────
function LoginScreen({ theme, onToggle }) {
  const { instance, inProgress } = useMsal();
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setLoading(true);
    try { await instance.loginRedirect(loginRequest); }
    catch (err) { console.error(err); setLoading(false); }
  }

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(var(--b1) 1px,transparent 1px),linear-gradient(90deg,var(--b1) 1px,transparent 1px)",
        backgroundSize: "40px 40px", opacity: 0.3,
      }}/>
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeToggle theme={theme} onToggle={onToggle}/>
      </div>
      <div style={{
        position: "relative", zIndex: 1,
        background: "var(--s1)", border: "1px solid var(--b2)",
        borderRadius: 16, padding: "48px 48px 40px",
        width: 440, textAlign: "center", animation: "fadeIn .3s ease",
        boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
      }}>
        {/* Real IMA logo */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <IMALogo theme={theme}/>
        </div>
        <div style={{
          display: "inline-block",
          background: "var(--primary-dim)",
          border: "1px solid rgba(0,178,227,0.3)",
          borderRadius: 6, padding: "3px 10px",
          fontSize: 10, color: "var(--primary)",
          fontFamily: "var(--mono)", letterSpacing: ".06em", marginBottom: 16,
        }}>
          SECURITY RESPONSE SUITE
        </div>
        <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 32, lineHeight: 1.8 }}>
          Phishing purge · Mailbox delegation<br/>
          Exfiltration audit · Message trace<br/>
          User containment · Threat intelligence
        </div>
        <button
          onClick={handleLogin}
          disabled={loading || inProgress !== InteractionStatus.None}
          style={{
            width: "100%", padding: "13px 0",
            background: loading ? "var(--b2)" : "var(--ima-blue)",
            border: "none", borderRadius: "var(--r)",
            color: "#fff", fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            transition: "background .15s",
          }}
        >
          {loading && <span style={{
            width: 14, height: 14,
            border: "2px solid rgba(255,255,255,0.4)",
            borderTopColor: "#fff", borderRadius: "50%",
            animation: "spin .7s linear infinite",
          }}/>}
          {loading ? "Redirecting to Microsoft…" : "Sign in with Microsoft 365"}
        </button>
        <div style={{ marginTop: 16, fontSize: 11, color: "var(--tx3)", lineHeight: 1.7 }}>
          Only <strong style={{ color: "var(--tx2)" }}>@theimagroup.com</strong> accounts can access this tool.
        </div>
        <div style={{
          marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--b1)",
          display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap",
        }}>
          {["Mail.ReadWrite", "AuditLog.Read", "Sites.ReadWrite", "User.ReadWrite"].map(p => (
            <div key={p} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "var(--tx3)" }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)", display: "inline-block" }}/>
              {p}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── App shell ────────────────────────────────────────────────────────────────
// KEY FIX: Pages are rendered with display:none instead of unmounted.
// This keeps all React state (search results, phase, form) alive across tab switches.
function AppShell({ theme, onToggle }) {
  const { instance } = useMsal();
  const [tab, setTab] = useState("purge");
  const [user, setUser] = useState(null);
  const [scans, setScans] = useState({});

  useEffect(() => {
    getCurrentUser().then(setUser).catch(() => {});
  }, []);

  function updateScan(id, data) {
    setScans(s =>
      data === null
        ? (({ [id]: _, ...rest }) => rest)(s)
        : { ...s, [id]: { id, ...data } }
    );
  }

  const pages = {
    purge:    PurgePage,
    delegate: DelegatePage,
    exfil:    ExfilPage,
    trace:    TracePage,
    threat:   ThreatPage,
    contain:  ContainPage,
    log:      LogPage,
  };

  const activeTab = TABS.find(t => t.id === tab);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Topbar */}
      <div style={{
        height: 54, background: "var(--s1)", borderBottom: "1px solid var(--b1)",
        display: "flex", alignItems: "center", padding: "0 20px", gap: 12, flexShrink: 0,
      }}>
        {/* Real IMA logo */}
        <div style={{ display: "flex", alignItems: "center", marginRight: 4, flexShrink: 0 }}>
          <IMALogo theme={theme}/>
        </div>

        <div style={{ width: 1, height: 24, background: "var(--b2)", flexShrink: 0 }}/>

        <div style={{
          fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)",
          letterSpacing: ".06em", textTransform: "uppercase", flexShrink: 0,
        }}>
          Response Suite
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 1, flex: 1, overflowX: "auto", marginLeft: 8 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 11px", borderRadius: "var(--r)",
              border: "1px solid " + (tab === t.id ? "var(--b2)" : "transparent"),
              background: tab === t.id ? "var(--s3)" : "transparent",
              color: tab === t.id ? "var(--tx)" : "var(--tx3)",
              fontFamily: "var(--sans)", fontSize: 11,
              fontWeight: tab === t.id ? 600 : 400,
              cursor: "pointer", whiteSpace: "nowrap", transition: "all .15s",
              position: "relative",
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: "50%",
                background: tab === t.id ? t.color : "var(--b3)",
                transition: "background .15s",
                animation: scans[t.id]?.running ? "pulse 1s infinite" : "none",
              }}/>
              {t.label}
              {/* Running indicator dot */}
              {scans[t.id]?.running && (
                <span style={{
                  position: "absolute", top: 3, right: 3,
                  width: 5, height: 5, borderRadius: "50%",
                  background: "var(--primary)",
                  animation: "pulse 1s infinite",
                }}/>
              )}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <ThemeToggle theme={theme} onToggle={onToggle}/>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "4px 10px", background: "var(--s2)", border: "1px solid var(--b1)",
            borderRadius: 999, fontSize: 11, color: "var(--tx2)", whiteSpace: "nowrap",
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)" }}/>
            {user?.mail || "Loading…"}
          </div>
          <button onClick={() => instance.logoutRedirect()} style={{
            background: "transparent", border: "1px solid var(--b2)",
            borderRadius: "var(--r)", color: "var(--tx3)",
            fontFamily: "var(--sans)", fontSize: 11, padding: "5px 10px", cursor: "pointer",
          }}>
            Sign out
          </button>
        </div>
      </div>

      {/* Active tab accent bar */}
      <div style={{ height: 2, background: "var(--b1)", flexShrink: 0 }}>
        <div style={{
          height: "100%", background: activeTab?.color || "var(--primary)",
          transition: "all .3s ease",
          width: `${(TABS.findIndex(t => t.id === tab) + 1) * (100 / TABS.length)}%`,
        }}/>
      </div>

      {/* Background operation indicator */}
      <GlobalScanBar scans={scans}/>

      {/* 
        KEY FIX: All pages are always mounted. 
        Inactive pages are hidden with display:none so their state survives tab switches.
        Search results, form data, and phase are all preserved when switching tabs.
      */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {TABS.map(t => {
          const Page = pages[t.id];
          return (
            <div
              key={t.id}
              style={{
                display: tab === t.id ? "block" : "none",
                padding: "24px 28px",
                minHeight: "100%",
              }}
            >
              <Page user={user} onScanUpdate={updateScan}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("ima-theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ima-theme", theme);
  }, [theme]);

  return (
    <>
      <AuthenticatedTemplate>
        <AppShell theme={theme} onToggle={() => setTheme(t => t === "dark" ? "light" : "dark")}/>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen theme={theme} onToggle={() => setTheme(t => t === "dark" ? "light" : "dark")}/>
      </UnauthenticatedTemplate>
    </>
  );
}
